import { at, uid } from "@/lib/format";

// ---------------------------------------------------------------------------
// Askilia prototype seed data — a fictional private clinic in Tbilisi.
// All data is invented. Nothing here calls a model; Ilia's replies are scripted.
// ---------------------------------------------------------------------------

export const CLINIC = {
  name: "Askilia",
  workspace: "Vake Medical Clinic",
  city: "Tbilisi",
  timezone: "Asia/Tbilisi",
  currency: "GEL",
  hours: "Mon-Sat, 10:00-19:00",
};

export const USER = { id: "u1", name: "Giorgi Beridze", role: "Owner", initials: "GB" };

export const TEAM = [
  { id: "u1", name: "Giorgi Beridze", role: "Owner", email: "giorgi@vakeclinic.ge", initials: "GB", online: true },
  { id: "u2", name: "Tamuna Kvaratskhelia", role: "Manager", email: "tamuna@vakeclinic.ge", initials: "TK", online: true },
  { id: "u3", name: "Ana Lomtadze", role: "Agent", email: "ana@vakeclinic.ge", initials: "AL", online: false },
];

// Calendar resources are deliberately separate. A booking snapshots one
// practitioner and one room so either can be used as the planning lens.
export const RESOURCES = [
  { id: "r-nino", name: "Dr. Nino Tsereteli", short: "Dr. Nino", role: "Cardiologist", type: "person" },
  { id: "r-temo", name: "Dr. Temo Japaridze", short: "Dr. Temo", role: "Cardiologist", type: "person" },
  { id: "r-george", name: "Dr. George Kandelaki", short: "Dr. George", role: "Cardiologist", type: "person" },
  { id: "r-lika", name: "Dr. Lika Abashidze", short: "Dr. Lika", role: "Dermatologist", type: "person" },
  { id: "r-maia", name: "Dr. Maia Orbeliani", short: "Dr. Maia", role: "Radiologist", type: "person" },
  { id: "r-ana-lab", name: "Dr. Ana Revazishvili", short: "Dr. Ana", role: "Laboratory medicine", type: "person" },
  { id: "r-consult-a", name: "Consultation Room A", short: "Consult Room A", role: "Consultation room, floor 2", type: "room" },
  { id: "r-consult-b", name: "Consultation Room B", short: "Consult Room B", role: "Consultation room, floor 2", type: "room" },
  { id: "r-ecg", name: "ECG Room", short: "ECG Room", role: "Room · 1 station", type: "room" },
  { id: "r-ultrasound", name: "Ultrasound Room", short: "Ultrasound", role: "Room · 1 scanner", type: "room" },
  { id: "r-lab", name: "Sample Collection Room", short: "Lab Room", role: "Room · 2 chairs", type: "room" },
];

export const SERVICES = [
  { id: "s-cardio", name: "Cardiology consultation", description: "A consultation with a heart specialist, with an ECG room reserved when needed.", duration: 30, price: 120, resource: "r-nino", room: "r-consult-a", doctorOptions: ["r-nino", "r-temo", "r-george"], roomOptions: ["r-consult-a", "r-consult-b", "r-ecg"], requiresDoctor: true, requiresRoom: true, buffer: 15, source: "imported" },
  { id: "s-ecg", name: "ECG", description: "A short test that records the heart's electrical activity.", duration: 15, price: 40, resource: "r-temo", room: "r-ecg", doctorOptions: ["r-nino", "r-temo", "r-george"], roomOptions: ["r-ecg"], requiresDoctor: true, requiresRoom: true, buffer: 10, source: "imported" },
  { id: "s-holter", name: "Holter monitoring, 24h", description: "A small portable heart monitor worn for 24 hours to record heart rhythm during normal daily activity.", duration: 20, price: 150, resource: "r-nino", room: "r-ecg", doctorOptions: ["r-nino", "r-temo", "r-george"], roomOptions: ["r-ecg"], requiresDoctor: true, requiresRoom: true, buffer: 10, source: "imported" },
  { id: "s-derma", name: "Dermatology consultation", description: "A skin, hair, or nail consultation with a dermatologist.", duration: 30, price: 90, resource: "r-lika", room: "r-consult-b", doctorOptions: ["r-lika"], roomOptions: ["r-consult-a", "r-consult-b"], requiresDoctor: true, requiresRoom: true, buffer: 10, source: "imported" },
  { id: "s-thyroid", name: "Thyroid ultrasound", description: "An ultrasound scan that checks the thyroid gland in the neck.", duration: 25, price: 75, resource: "r-maia", room: "r-ultrasound", doctorOptions: ["r-maia"], roomOptions: ["r-ultrasound"], requiresDoctor: true, requiresRoom: true, buffer: 10, source: "review" },
  { id: "s-blood", name: "Blood test panel", description: "A standard group of blood tests collected during one visit.", duration: 15, price: 60, resource: "r-ana-lab", room: "r-lab", doctorOptions: ["r-ana-lab"], roomOptions: ["r-lab"], requiresDoctor: true, requiresRoom: true, buffer: 5, source: "imported" },
];

// Recurring room duties are the operational bridge between the two calendar
// lenses. They answer both "who is working in this room?" and "which room is
// this doctor using?". Weekdays follow JavaScript's convention (Sun = 0).
// A booking must fit inside one block, including its protected buffer.
export const ROOM_DUTIES = [
  {
    id: "duty-nino-consult-a-mon-wed-fri",
    doctorId: "r-nino",
    roomId: "r-consult-a",
    weekdays: [1, 3, 5],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-cardio"],
    source: "imported",
  },
  {
    id: "duty-nino-ecg-tue-thu-sat",
    doctorId: "r-nino",
    roomId: "r-ecg",
    weekdays: [2, 4, 6],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-cardio", "s-ecg", "s-holter"],
    source: "imported",
  },
  {
    id: "duty-temo-ecg-mon-wed-fri-morning",
    doctorId: "r-temo",
    roomId: "r-ecg",
    weekdays: [1, 3, 5],
    start: "10:00",
    end: "15:00",
    serviceIds: ["s-cardio", "s-ecg", "s-holter"],
    source: "imported",
  },
  {
    id: "duty-george-ecg-mon-wed-fri-afternoon",
    doctorId: "r-george",
    roomId: "r-ecg",
    weekdays: [1, 3, 5],
    start: "15:00",
    end: "19:00",
    serviceIds: ["s-cardio", "s-ecg", "s-holter"],
    source: "imported",
  },
  {
    id: "duty-temo-consult-a-tue-thu-sat",
    doctorId: "r-temo",
    roomId: "r-consult-a",
    weekdays: [2, 4, 6],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-cardio"],
    source: "imported",
  },
  {
    id: "duty-lika-consult-b",
    doctorId: "r-lika",
    roomId: "r-consult-b",
    weekdays: [1, 2, 3, 4, 5, 6],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-derma"],
    source: "imported",
  },
  {
    id: "duty-maia-ultrasound",
    doctorId: "r-maia",
    roomId: "r-ultrasound",
    weekdays: [1, 2, 3, 4, 5, 6],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-thyroid"],
    source: "imported",
  },
  {
    id: "duty-ana-lab",
    doctorId: "r-ana-lab",
    roomId: "r-lab",
    weekdays: [1, 2, 3, 4, 5, 6],
    start: "10:00",
    end: "19:00",
    serviceIds: ["s-blood"],
    source: "imported",
  },
];

export const CHANNELS = [
  { id: "ch-ig", name: "Instagram", handle: "@vakeclinic", status: "connected", color: "accent" },
  { id: "ch-wa", name: "WhatsApp", handle: "+995 32 2 11 07 07", status: "connected", color: "success" },
  { id: "ch-me", name: "Messenger", handle: "Vake Clinic", status: "expired", color: "human" },
];

export const STAGES = ["New", "Qualifying", "Qualified", "Offered", "Booked/Won"];
export const CLOSED_STAGES = ["Not now", "Lost", "Disqualified"];

const demoInitials = (name) => name
  .split(" ")
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase();

function demoCustomer({
  id,
  name,
  ageBand,
  channel,
  handle,
  phone,
  email = "",
  stage,
  owner = null,
  serviceId,
  value,
  lastDay,
  lastHour,
  visits = 0,
  noShows = 0,
  moved = 0,
  spent = 0,
  marketing = false,
  insurance = null,
  nextAction = null,
  extraChannels = [],
  qualification = [],
  notes = [],
}) {
  const service = SERVICES.find((item) => item.id === serviceId);
  const channels = [{ type: channel, handle }, ...extraChannels];
  const openValue = Number(value ?? service?.price ?? 0);
  const projectedVisits = visits >= 3 ? 3 : 2;
  return {
    id,
    name,
    initials: demoInitials(name),
    lang: "ka",
    ageBand,
    channels,
    phone,
    email,
    consent: { messaging: true, marketing, updated: at(Math.min(lastDay - 14, -7), 11) },
    stage,
    owner,
    value: openValue,
    valueSource: "imported",
    valueBasis: service?.name || "Current opportunity",
    source: channel,
    lastInteraction: at(lastDay, lastHour),
    nextAction,
    insurance,
    first: visits === 0,
    history: { visits, noShows, moved },
    commercial: {
      spent,
      openValue,
      lifetimeValue: spent + openValue * projectedVisits,
      lifetimeBasis: `Imported spend plus ${projectedVisits} likely ${service?.name || "follow-up"} visits at the current list price.`,
    },
    qualification: [
      { key: "Service", value: service?.name || "General enquiry", source: "customer" },
      ...qualification,
    ],
    notes,
  };
}

// ---------------------------------------------------------------------------
// Customers / Opportunities
// ---------------------------------------------------------------------------
export const CUSTOMERS = [
  {
    id: "cust-nino", name: "Nino Tsiklauri", initials: "NT", lang: "ka", ageBand: "25-34",
    channels: [{ type: "Instagram", handle: "@nino.ts" }], phone: "+995 599 18 24 70", email: "",
    consent: { messaging: true, marketing: false, updated: at(-9, 12) },
    stage: "New", owner: null, value: 120, valueSource: "imported", valueBasis: "Cardiology consultation", source: "Instagram",
    lastInteraction: at(0, -0.1),
    nextAction: { label: "Qualify the enquiry", due: "today", owner: "Ilia" },
    insurance: null, first: true,
    history: { visits: 0, noShows: 0, moved: 0 },
    commercial: { spent: 0, openValue: 120, lifetimeValue: 480, lifetimeBasis: "Estimate based on four cardiology visits at the current list price." },
    qualification: [{ key: "Interest", value: "Cardiology", source: "customer" }],
    notes: [],
  },
  {
    id: "cust-beka", name: "Beka Lomidze", initials: "BL", lang: "ka", ageBand: "35-44",
    channels: [{ type: "WhatsApp", handle: "+995 555 03 71 19" }], phone: "+995 555 03 71 19", email: "beka.l@gmail.com",
    consent: { messaging: true, marketing: true, updated: at(-30, 10) },
    stage: "Qualifying", owner: "Giorgi Beridze", value: 150, valueSource: "imported", valueBasis: "Holter monitoring, 24h", source: "WhatsApp",
    lastInteraction: at(0, -0.2),
    nextAction: { label: "Reply about Aldagi cover", due: "today", owner: "Giorgi Beridze" },
    insurance: "Aldagi", first: false,
    history: { visits: 1, noShows: 0, moved: 0 },
    commercial: { spent: 90, openValue: 150, lifetimeValue: null, lifetimeBasis: "One previous visit and a Holter test is usually a one-off diagnostic. There is not enough history to project yet." },
    qualification: [
      { key: "Service", value: "Holter monitoring, 24h", source: "customer" },
      { key: "Insurance", value: "Aldagi (unconfirmed cover)", source: "customer" },
    ],
    notes: [{ id: uid("n"), by: "Tamuna Kvaratskhelia", ts: at(-1, 15), text: "Asked about Holter last month too — price sensitive." }],
  },
  {
    id: "cust-mari", name: "Mari Kapanadze", initials: "MK", lang: "ka", ageBand: "25-34",
    channels: [{ type: "Instagram", handle: "@mari.k" }], phone: "+995 574 90 33 08", email: "",
    consent: { messaging: true, marketing: false, updated: at(-40, 11) },
    stage: "Booked/Won", owner: "Ilia", value: 90, valueSource: "imported", valueBasis: "Dermatology consultation", source: "Instagram",
    lastInteraction: at(0, -1.5),
    nextAction: { label: "Handle reschedule request", due: "today", owner: "Giorgi Beridze" },
    insurance: null, first: false,
    history: { visits: 3, noShows: 0, moved: 1 },
    commercial: { spent: 270, openValue: 90, lifetimeValue: 360, lifetimeBasis: "Estimate based on one dermatology visit per year for four years at the current list price." },
    qualification: [{ key: "Service", value: "Dermatology consultation", source: "customer" }],
    notes: [],
  },
  {
    id: "cust-giga", name: "Giga Ramishvili", initials: "GR", lang: "ka", ageBand: "45-54",
    channels: [{ type: "WhatsApp", handle: "+995 599 41 22 08" }], phone: "+995 599 41 22 08", email: "",
    consent: { messaging: true, marketing: false, updated: at(-3, 9) },
    stage: "Offered", owner: null, value: 850, valueSource: "customer", valueBasis: "Full cardiac package discussed in chat", source: "WhatsApp",
    lastInteraction: at(0, -0.4),
    nextAction: { label: "Decide on instalment request", due: "today", owner: "Giorgi Beridze" },
    insurance: null, first: false,
    history: { visits: 2, noShows: 0, moved: 0 },
    commercial: { spent: 160, openValue: 850, lifetimeValue: 1170, lifetimeBasis: "Current package plus imported spend from two prior visits." },
    qualification: [{ key: "Interest", value: "Full cardiac package", source: "customer" }, { key: "Budget note", value: "Asked to pay in two parts", source: "customer" }],
    notes: [],
  },
  {
    id: "cust-tamar", name: "Tamar Gelashvili", initials: "TG", lang: "ka", ageBand: "45-54",
    channels: [{ type: "Instagram", handle: "@tamar.g" }], phone: "+995 595 44 76 30", email: "",
    consent: { messaging: true, marketing: false, updated: at(-60, 18) },
    stage: "Qualified", owner: null, value: 75, valueSource: "imported", valueBasis: "Thyroid ultrasound", source: "Instagram",
    lastInteraction: at(-2, 19),
    nextAction: null,
    insurance: null, first: false,
    history: { visits: 0, noShows: 0, moved: 0 },
    commercial: { spent: 0, openValue: 75, lifetimeValue: 335, lifetimeBasis: "Thyroid ultrasound plus an estimated four follow-up blood panels." },
    qualification: [{ key: "Interest", value: "Thyroid ultrasound", source: "customer" }, { key: "Pattern", value: "Only writes in the evening", source: "ilia" }],
    notes: [{ id: uid("n"), by: "Ilia", ts: at(-2, 19), text: "Asked about thyroid check 3 times since June, booked none." }],
  },
  {
    id: "cust-salome", name: "Salome Datunashvili", initials: "SD", lang: "ka", ageBand: "55-64",
    channels: [{ type: "WhatsApp", handle: "+995 574 62 18 90" }], phone: "+995 574 62 18 90", email: "salome.d@gmail.com",
    consent: { messaging: true, marketing: true, updated: at(-90, 12) },
    stage: "Booked/Won", owner: "Ilia", value: 120, valueSource: "imported", valueBasis: "Cardiology consultation", source: "WhatsApp",
    lastInteraction: at(-1, 16),
    nextAction: null,
    insurance: "TBC Insurance", first: false,
    history: { visits: 4, noShows: 0, moved: 0 },
    commercial: { spent: 480, openValue: 120, lifetimeValue: 1440, lifetimeBasis: "Estimate based on her current visit pattern continuing for two more years." },
    qualification: [{ key: "Service", value: "Cardiology consultation", source: "customer" }],
    notes: [],
  },
  {
    id: "cust-lasha", name: "Lasha Gogichaishvili", initials: "LG", lang: "ka", ageBand: "35-44",
    channels: [{ type: "Instagram", handle: "@lasha.g" }, { type: "WhatsApp", handle: "+995 558 44 12 90" }], phone: "+995 558 44 12 90", email: "",
    consent: { messaging: true, marketing: false, updated: at(-5, 12) },
    stage: "Qualifying", owner: "Ilia", value: 75, valueSource: "imported", valueBasis: "Thyroid ultrasound", source: "Instagram",
    lastInteraction: at(0, -3),
    nextAction: { label: "Confirm identity merge", due: "today", owner: "Ilia" },
    insurance: null, first: false, mergePending: { channel: "WhatsApp", reason: "same phone number" },
    history: { visits: 1, noShows: 0, moved: 0 },
    commercial: { spent: 75, openValue: 75, lifetimeValue: 375, lifetimeBasis: "Estimate based on one thyroid check per year for five years." },
    qualification: [{ key: "Interest", value: "Thyroid ultrasound", source: "customer" }],
    notes: [],
  },
  demoCustomer({
    id: "cust-ana-beridze", name: "Ana Beridze", ageBand: "25-34", channel: "Instagram", handle: "@ana.beridze",
    phone: "+995 599 12 08 41", email: "ana.beridze@gmail.com", stage: "Qualified", owner: "Ilia", serviceId: "s-derma",
    value: 90, lastDay: -6, lastHour: 18, visits: 3, spent: 270, marketing: true,
    qualification: [{ key: "Pattern", value: "Books preventive skin checks each year", source: "observed" }],
  }),
  demoCustomer({
    id: "cust-luka-maisuradze", name: "Luka Maisuradze", ageBand: "45-54", channel: "WhatsApp", handle: "+995 577 23 19 44",
    phone: "+995 577 23 19 44", email: "luka.m@icloud.com", stage: "Booked/Won", owner: "Ilia", serviceId: "s-cardio",
    value: 120, lastDay: -5, lastHour: 10, visits: 3, spent: 360, marketing: true, insurance: "GPI",
    extraChannels: [{ type: "Telegram", handle: "@lukamais" }],
  }),
  demoCustomer({
    id: "cust-eka-khutsishvili", name: "Eka Khutsishvili", ageBand: "35-44", channel: "Messenger", handle: "Eka Khutsishvili",
    phone: "+995 568 35 10 62", stage: "Qualifying", owner: "Ilia", serviceId: "s-thyroid",
    value: 75, lastDay: -8, lastHour: 13, visits: 1, spent: 75,
    nextAction: { label: "Ask about preferred weekday", due: "this week", owner: "Ilia" },
  }),
  demoCustomer({
    id: "cust-davit-abashidze", name: "Davit Abashidze", ageBand: "55-64", channel: "WhatsApp", handle: "+995 551 42 08 75",
    phone: "+995 551 42 08 75", email: "d.abashidze@mail.ge", stage: "Qualified", owner: "Tamuna Kvaratskhelia", serviceId: "s-ecg",
    value: 40, lastDay: -12, lastHour: 15, visits: 2, spent: 80,
    nextAction: { label: "Offer cardiology follow-up", due: "this week", owner: "Tamuna Kvaratskhelia" },
  }),
  demoCustomer({
    id: "cust-tea-kvirikashvili", name: "Tea Kvirikashvili", ageBand: "25-34", channel: "Instagram", handle: "@tea.kviri",
    phone: "+995 593 61 22 05", stage: "Booked/Won", owner: "Ilia", serviceId: "s-derma",
    value: 90, lastDay: -3, lastHour: 17, visits: 2, moved: 1, spent: 180, marketing: true,
    qualification: [{ key: "Preferred time", value: "Late afternoon", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-giorgi-mchedlishvili", name: "Giorgi Mchedlishvili", ageBand: "45-54", channel: "WhatsApp", handle: "+995 555 88 31 40",
    phone: "+995 555 88 31 40", stage: "Qualifying", owner: "Giorgi Beridze", serviceId: "s-holter",
    value: 150, lastDay: -7, lastHour: 12, visits: 1, spent: 150, insurance: "Ardi",
    nextAction: { label: "Check whether symptoms continued", due: "this week", owner: "Giorgi Beridze" },
    qualification: [{ key: "Insurance", value: "Ardi", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-ketevan-nadiradze", name: "Ketevan Nadiradze", ageBand: "55-64", channel: "Messenger", handle: "Ketevan Nadiradze",
    phone: "+995 599 73 14 28", email: "ketevan.n@gmail.com", stage: "Booked/Won", owner: "Ilia", serviceId: "s-blood",
    value: 60, lastDay: -1, lastHour: 11, visits: 2, spent: 120, marketing: true,
    extraChannels: [{ type: "WhatsApp", handle: "+995 599 73 14 28" }],
  }),
  demoCustomer({
    id: "cust-irakli-japaridze", name: "Irakli Japaridze", ageBand: "35-44", channel: "WhatsApp", handle: "+995 577 09 66 12",
    phone: "+995 577 09 66 12", stage: "Booked/Won", owner: "Ilia", serviceId: "s-cardio",
    value: 120, lastDay: -2, lastHour: 9, visits: 0, noShows: 1, spent: 0, insurance: "Ardi",
    qualification: [{ key: "Preferred time", value: "Morning before work", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-natia-shengelia", name: "Natia Shengelia", ageBand: "45-54", channel: "Instagram", handle: "@natiashengelia",
    phone: "+995 591 30 54 17", stage: "Booked/Won", owner: "Ilia", serviceId: "s-thyroid",
    value: 75, lastDay: -2, lastHour: 19, visits: 1, spent: 75, marketing: true,
  }),
  demoCustomer({
    id: "cust-sandro-kapanadze", name: "Sandro Kapanadze", ageBand: "25-34", channel: "Messenger", handle: "Sandro Kapanadze",
    phone: "+995 568 77 02 96", stage: "Not now", owner: null, serviceId: "s-ecg",
    value: 40, lastDay: -14, lastHour: 16, visits: 1, spent: 40,
    qualification: [{ key: "Outcome", value: "Will return after travel", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-maia-gelashvili", name: "Maia Gelashvili", ageBand: "35-44", channel: "Instagram", handle: "@maia.gelashvili",
    phone: "+995 595 26 47 03", email: "maia.g@mail.ge", stage: "Offered", owner: "Tamuna Kvaratskhelia", serviceId: "s-derma",
    value: 90, lastDay: -4, lastHour: 18, visits: 1, spent: 90, marketing: true,
    nextAction: { label: "Confirm next consultation", due: "this week", owner: "Tamuna Kvaratskhelia" },
    extraChannels: [{ type: "WhatsApp", handle: "+995 595 26 47 03" }],
  }),
  demoCustomer({
    id: "cust-levan-chikovani", name: "Levan Chikovani", ageBand: "45-54", channel: "WhatsApp", handle: "+995 557 18 73 42",
    phone: "+995 557 18 73 42", stage: "Offered", owner: "Giorgi Beridze", serviceId: "s-holter",
    value: 150, lastDay: -3, lastHour: 14, visits: 1, spent: 150,
    nextAction: { label: "Confirm Saturday appointment", due: "today", owner: "Giorgi Beridze" },
    qualification: [{ key: "Preferred time", value: "Saturday afternoon", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-nino-kiknadze", name: "Nino Kiknadze", ageBand: "55-64", channel: "WhatsApp", handle: "+995 599 48 62 20",
    phone: "+995 599 48 62 20", email: "nino.kiknadze@gmail.com", stage: "Booked/Won", owner: "Ilia", serviceId: "s-cardio",
    value: 120, lastDay: -2, lastHour: 12, visits: 1, spent: 120, insurance: "TBC Insurance",
  }),
  demoCustomer({
    id: "cust-elene-dolidze", name: "Elene Dolidze", ageBand: "25-34", channel: "Instagram", handle: "@elene.dolidze",
    phone: "+995 574 33 75 11", stage: "New", owner: "Ilia", serviceId: "s-blood",
    value: 60, lastDay: -1, lastHour: 20, visits: 0, noShows: 1, spent: 0,
    nextAction: { label: "Confirm held slot", due: "this week", owner: "Ilia" },
  }),
  demoCustomer({
    id: "cust-zurab-tsereteli", name: "Zurab Tsereteli", ageBand: "65+", channel: "WhatsApp", handle: "+995 599 05 19 83",
    phone: "+995 599 05 19 83", email: "z.tsereteli@mail.ge", stage: "Booked/Won", owner: "Ilia", serviceId: "s-cardio",
    value: 120, lastDay: -4, lastHour: 10, visits: 1, spent: 120, marketing: true,
  }),
  demoCustomer({
    id: "cust-sopio-kharadze", name: "Sopio Kharadze", ageBand: "35-44", channel: "Messenger", handle: "Sopio Kharadze",
    phone: "+995 558 64 09 28", stage: "New", owner: null, serviceId: "s-derma",
    value: 90, lastDay: -2, lastHour: 16, visits: 0, moved: 1, spent: 0,
    nextAction: { label: "Confirm preferred dermatologist", due: "this week", owner: "Ilia" },
    qualification: [{ key: "Preferred time", value: "Flexible after 14:00", source: "customer" }],
  }),
  demoCustomer({
    id: "cust-vakhtang-lomidze", name: "Vakhtang Lomidze", ageBand: "45-54", channel: "WhatsApp", handle: "+995 555 71 38 06",
    phone: "+995 555 71 38 06", stage: "Qualified", owner: "Tamuna Kvaratskhelia", serviceId: "s-holter",
    value: 150, lastDay: -9, lastHour: 11, visits: 1, spent: 150,
    nextAction: { label: "Offer ECG follow-up", due: "next week", owner: "Tamuna Kvaratskhelia" },
  }),
  demoCustomer({
    id: "cust-rusudan-gogua", name: "Rusudan Gogua", ageBand: "55-64", channel: "WhatsApp", handle: "+995 577 92 10 36",
    phone: "+995 577 92 10 36", stage: "Qualifying", owner: "Giorgi Beridze", serviceId: "s-thyroid",
    value: 75, lastDay: -11, lastHour: 17, visits: 0, noShows: 1, spent: 0,
    nextAction: { label: "Start no-show recovery", due: "today", owner: "Giorgi Beridze" },
  }),
  demoCustomer({
    id: "cust-tornike-metreveli", name: "Tornike Metreveli", ageBand: "25-34", channel: "Instagram", handle: "@tornike.met",
    phone: "+995 591 84 55 29", stage: "New", owner: "Ilia", serviceId: "s-ecg",
    value: 40, lastDay: -3, lastHour: 21, visits: 1, spent: 40,
    nextAction: { label: "Qualify the enquiry", due: "this week", owner: "Ilia" },
  }),
  demoCustomer({
    id: "cust-salome-iashvili", name: "Salome Iashvili", ageBand: "35-44", channel: "Messenger", handle: "Salome Iashvili",
    phone: "+995 568 12 49 70", email: "salome.i@gmail.com", stage: "Lost", owner: null, serviceId: "s-cardio",
    value: 120, lastDay: -18, lastHour: 10, visits: 1, spent: 120, marketing: true,
    extraChannels: [{ type: "Telegram", handle: "@salomei" }],
    qualification: [{ key: "Outcome", value: "Chose a clinic closer to work", source: "customer" }],
  }),
];

// ---------------------------------------------------------------------------
// Bookings — calendar events (relative to this week)
// ---------------------------------------------------------------------------
function demoBooking({ id, customerId, serviceId, day, hour, state, probability, source = "human", doctorId = null, roomId = null }) {
  const service = SERVICES.find((item) => item.id === serviceId);
  return {
    id,
    seedVersion: 5,
    customerId,
    serviceId,
    resourceId: doctorId || roomId || null,
    doctorId,
    roomId,
    start: at(day, hour),
    end: at(day, hour + service.duration / 60),
    buffer: service.buffer,
    state,
    source,
    price: service.price,
    risk: probability == null ? 0 : Math.max(0, 100 - probability),
    ...(probability == null ? {} : { showUpProbability: probability, showUpSource: "prediction" }),
  };
}

function snapshotBookingAssignments(booking) {
  const service = SERVICES.find((item) => item.id === booking.serviceId);
  const startsAt = new Date(booking.start);
  const heldUntil = new Date(new Date(booking.end).getTime() + Number(booking.buffer || 0) * 60000);
  const clock = (value) => {
    const [hours, minutes] = String(value).split(":").map(Number);
    return hours * 60 + minutes;
  };
  const startMinute = startsAt.getHours() * 60 + startsAt.getMinutes();
  const heldUntilMinute = heldUntil.getDate() === startsAt.getDate()
    ? heldUntil.getHours() * 60 + heldUntil.getMinutes()
    : 24 * 60;
  const coveringDuties = ROOM_DUTIES.filter((duty) => (
    duty.weekdays?.includes(startsAt.getDay())
    && (!duty.serviceIds?.length || duty.serviceIds.includes(booking.serviceId))
    && clock(duty.start) <= startMinute
    && clock(duty.end) >= heldUntilMinute
  ));
  const legacyResource = RESOURCES.find((item) => item.id === booking.resourceId);
  const explicitDoctorId = booking.doctorId
    || (legacyResource?.type === "person" ? legacyResource.id : null);
  const explicitRoomId = booking.roomId
    || (legacyResource?.type === "room" ? legacyResource.id : null);
  const duty = coveringDuties.find((item) => (
    (!explicitDoctorId || item.doctorId === explicitDoctorId)
    && (!explicitRoomId || item.roomId === explicitRoomId)
  )) || (!explicitDoctorId && !explicitRoomId ? coveringDuties[0] : null);
  const doctorId = explicitDoctorId || duty?.doctorId || service?.resource || null;
  const roomId = explicitRoomId || duty?.roomId || service?.room || null;
  return {
    ...booking,
    seedVersion: 5,
    resourceId: doctorId,
    doctorId,
    roomId,
    resourceIds: Array.from(new Set([doctorId, roomId].filter(Boolean))),
  };
}

const BOOKING_ROWS = [
  { id: "bk-1", customerId: "cust-salome", serviceId: "s-cardio", resourceId: "r-nino", start: at(0, 10), end: at(0, 10.5), buffer: 15, state: "confirmed", source: "ilia", price: 120, risk: 8, showUpProbability: 92, showUpSource: "prediction" },
  { id: "bk-2", customerId: "cust-mari", serviceId: "s-derma", resourceId: "r-lika", start: at(2, 11), end: at(2, 11.5), buffer: 10, state: "confirmed", source: "ilia", price: 90, risk: 12, showUpProbability: 88, showUpSource: "prediction", reschedule: true },
  { id: "bk-3", customerId: "cust-beka", serviceId: "s-holter", resourceId: "r-george", doctorId: "r-george", roomId: "r-ecg", start: at(0, 15), end: at(0, 15.33), buffer: 10, state: "pending", source: "ilia", price: 150, risk: 46, showUpProbability: 54, showUpSource: "prediction" },
  { id: "bk-4", customerId: "cust-giga", serviceId: "s-cardio", resourceId: "r-nino", start: at(0, 16), end: at(0, 16.5), buffer: 15, state: "confirmed", source: "human", price: 120, risk: 0, showUpProbability: 86, showUpSource: "prediction" },
  { id: "bk-5", customerId: "cust-lasha", serviceId: "s-thyroid", resourceId: "r-maia", start: at(-2, 12), end: at(-2, 12.42), buffer: 10, state: "completed", source: "ilia", price: 75, risk: 0 },
  { id: "bk-6", customerId: "cust-tamar", serviceId: "s-thyroid", resourceId: "r-maia", start: at(-3, 14), end: at(-3, 14.42), buffer: 10, state: "noshow", source: "ilia", price: 75, risk: 0 },
  { id: "bk-7", customerId: "cust-salome", serviceId: "s-cardio", resourceId: "r-temo", doctorId: "r-temo", roomId: "r-consult-a", start: at(-2, 11), end: at(-2, 11.5), buffer: 15, state: "completed", source: "ilia", price: 120, risk: 0 },
  { id: "bk-8", customerId: "cust-mari", serviceId: "s-derma", resourceId: "r-lika", start: at(-4, 15), end: at(-4, 15.5), buffer: 10, state: "completed", source: "human", price: 90, risk: 0 },
  { id: "bk-9", customerId: "cust-giga", serviceId: "s-ecg", resourceId: "r-ecg", start: at(0, 12), end: at(0, 12.25), buffer: 10, state: "confirmed", source: "human", price: 40, risk: 0, showUpProbability: 86, showUpSource: "prediction" },
  demoBooking({ id: "bk-10", customerId: "cust-ana-beridze", serviceId: "s-derma", day: -41, hour: 10, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-11", customerId: "cust-luka-maisuradze", serviceId: "s-cardio", day: -40, hour: 11, state: "completed" }),
  demoBooking({ id: "bk-12", customerId: "cust-eka-khutsishvili", serviceId: "s-thyroid", day: -39, hour: 10, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-13", customerId: "cust-davit-abashidze", serviceId: "s-ecg", day: -38, hour: 14, state: "completed" }),
  demoBooking({ id: "bk-14", customerId: "cust-tea-kvirikashvili", serviceId: "s-derma", day: -34, hour: 12, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-15", customerId: "cust-giorgi-mchedlishvili", serviceId: "s-holter", day: -33, hour: 15, state: "completed" }),
  demoBooking({ id: "bk-16", customerId: "cust-ketevan-nadiradze", serviceId: "s-blood", day: -32, hour: 10, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-17", customerId: "cust-irakli-japaridze", serviceId: "s-cardio", day: -31, hour: 11, state: "noshow", source: "ilia" }),
  demoBooking({ id: "bk-18", customerId: "cust-natia-shengelia", serviceId: "s-thyroid", day: -27, hour: 13, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-19", customerId: "cust-sandro-kapanadze", serviceId: "s-ecg", day: -26, hour: 16, state: "completed" }),
  demoBooking({ id: "bk-20", customerId: "cust-maia-gelashvili", serviceId: "s-derma", day: -25, hour: 10, state: "completed" }),
  demoBooking({ id: "bk-21", customerId: "cust-levan-chikovani", serviceId: "s-holter", day: -24, hour: 12, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-22", customerId: "cust-nino-kiknadze", serviceId: "s-cardio", day: -20, hour: 11, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-23", customerId: "cust-elene-dolidze", serviceId: "s-blood", day: -19, hour: 15, state: "noshow", source: "ilia" }),
  demoBooking({ id: "bk-24", customerId: "cust-zurab-tsereteli", serviceId: "s-cardio", day: -18, hour: 10, state: "completed" }),
  demoBooking({ id: "bk-25", customerId: "cust-sopio-kharadze", serviceId: "s-derma", day: -17, hour: 14, state: "cancelled", source: "ilia" }),
  demoBooking({ id: "bk-26", customerId: "cust-vakhtang-lomidze", serviceId: "s-holter", day: -13, hour: 11, state: "completed" }),
  demoBooking({ id: "bk-27", customerId: "cust-rusudan-gogua", serviceId: "s-thyroid", day: -12, hour: 16, state: "noshow", source: "ilia" }),
  demoBooking({ id: "bk-28", customerId: "cust-tornike-metreveli", serviceId: "s-ecg", day: -11, hour: 12, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-29", customerId: "cust-salome-iashvili", serviceId: "s-cardio", day: -6, hour: 10, state: "completed" }),
  demoBooking({ id: "bk-30", customerId: "cust-ana-beridze", serviceId: "s-derma", day: -5, hour: 14, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-31", customerId: "cust-luka-maisuradze", serviceId: "s-cardio", day: -4, hour: 16, state: "completed", source: "ilia" }),
  demoBooking({ id: "bk-32", customerId: "cust-ketevan-nadiradze", serviceId: "s-blood", day: 1, hour: 10, state: "confirmed", probability: 91, source: "ilia" }),
  demoBooking({ id: "bk-33", customerId: "cust-irakli-japaridze", serviceId: "s-cardio", day: 2, hour: 10, state: "confirmed", probability: 82, source: "ilia", doctorId: "r-nino", roomId: "r-consult-a" }),
  demoBooking({ id: "bk-34", customerId: "cust-natia-shengelia", serviceId: "s-thyroid", day: 3, hour: 14, state: "confirmed", probability: 89, source: "ilia" }),
  demoBooking({ id: "bk-35", customerId: "cust-sandro-kapanadze", serviceId: "s-ecg", day: 5, hour: 12, state: "confirmed", probability: 86 }),
  demoBooking({ id: "bk-36", customerId: "cust-maia-gelashvili", serviceId: "s-derma", day: 5, hour: 10, state: "confirmed", probability: 93 }),
  demoBooking({ id: "bk-37", customerId: "cust-levan-chikovani", serviceId: "s-holter", day: 8, hour: 15, state: "pending", probability: 58, source: "ilia" }),
  demoBooking({ id: "bk-38", customerId: "cust-nino-kiknadze", serviceId: "s-cardio", day: 10, hour: 11, state: "confirmed", probability: 90, source: "ilia", doctorId: "r-temo", roomId: "r-consult-a" }),
  demoBooking({ id: "bk-39", customerId: "cust-elene-dolidze", serviceId: "s-blood", day: 15, hour: 16, state: "hold", probability: 64, source: "ilia" }),
  demoBooking({ id: "bk-40", customerId: "cust-zurab-tsereteli", serviceId: "s-cardio", day: 19, hour: 10, state: "confirmed", probability: 87 }),
  demoBooking({ id: "bk-41", customerId: "cust-sopio-kharadze", serviceId: "s-derma", day: 26, hour: 13, state: "pending", probability: 67, source: "ilia" }),
];

export const BOOKINGS = BOOKING_ROWS.map(snapshotBookingAssignments);

// ---------------------------------------------------------------------------
// Conversations — the Inbox. Each carries lifecycle, ownership + a script.
//   lifecycle: ilia | handoff_requested | waiting_human | human | waiting_customer | resolved
// ---------------------------------------------------------------------------
function demoConversation({
  id,
  customerId,
  serviceId,
  channel,
  day,
  hour,
  lifecycle,
  owner,
  waitingOn = null,
  unread = false,
  urgency = "normal",
  customerText,
  responseText,
  responseFrom = "ilia",
  bookingId = null,
  draft = null,
  handoff = null,
  captured = [],
  archivedAt = null,
  archivedBy = null,
}) {
  const service = SERVICES.find((item) => item.id === serviceId);
  const messages = [
    { id: uid("m"), from: "customer", channel, text: customerText, ts: at(day, hour - 0.08), status: "read" },
  ];
  if (responseText) {
    messages.push({
      id: uid("m"),
      from: responseFrom,
      channel,
      text: responseText,
      ts: at(day, hour),
      status: "read",
      ...(responseFrom === "human" ? { author: owner } : {}),
    });
  }
  return {
    id,
    customerId,
    serviceId,
    channel,
    channels: [channel],
    lifecycle,
    owner,
    unread,
    urgency,
    waitingOn,
    snoozedUntil: null,
    lastActivity: at(day, hour),
    journey: null,
    scriptStep: 0,
    bookingId,
    holdId: null,
    draft,
    handoff,
    captured: [
      { key: "Service", value: service?.name || "General enquiry", source: "customer" },
      ...captured,
    ],
    messages,
    archivedAt,
    archivedBy,
  };
}

export const CONVERSATIONS = [
  // ---- JOURNEY 1: new lead -> valid booking (scripted, advances via Ilia) ----
  {
    id: "conv-nino", customerId: "cust-nino", serviceId: "s-cardio", channel: "Instagram", channels: ["Instagram"],
    lifecycle: "ilia", owner: "Ilia", unread: true, urgency: "high", waitingOn: null,
    snoozedUntil: null, lastActivity: at(0, -0.1), journey: "lead", scriptStep: 0,
    bookingId: null, holdId: null, draft: null, handoff: null,
    captured: [{ key: "Channel", value: "Instagram DM", source: "observed" }],
    messages: [
      { id: uid("m"), from: "customer", channel: "Instagram", text: "hi! do you have a cardiologist? how much is a consultation?", ts: at(0, -0.1), status: "read" },
    ],
    // beats applied one by one when the owner lets Ilia continue
    script: [
      {
        add: [{ from: "ilia", text: "Hello! Yes, Dr. Nino Tsereteli is our cardiologist. A consultation is \u20BE120 for 30 minutes. May I ask two quick things to find the right times? Is this your first visit, and do you have insurance?" }],
        capture: [{ key: "Service", value: "Cardiology consultation", source: "ilia" }],
      },
      {
        add: [{ from: "customer", channel: "Instagram", text: "first visit yes. I have Aldagi. Friday morning would be best" }],
        capture: [
          { key: "First visit", value: "Yes", source: "customer" },
          { key: "Insurance", value: "Aldagi", source: "customer" },
          { key: "Preferred time", value: "Friday morning", source: "customer" },
        ],
      },
      {
        add: [{ from: "ilia", text: "Perfect. I found three available times with Dr. Nino. Choose the one that works best, then I will ask you to confirm before anything is booked." }],
        offerSlots: true,
      },
    ],
  },

  // ---- JOURNEY 2: safe human handoff (insurance/money) ----
  {
    id: "conv-beka", customerId: "cust-beka", serviceId: "s-holter", channel: "WhatsApp", channels: ["WhatsApp"],
    lifecycle: "waiting_human", owner: "Giorgi Beridze", unread: true, urgency: "high", waitingOn: "team",
    snoozedUntil: null, lastActivity: at(0, -0.2), journey: "handoff", scriptStep: 0,
    bookingId: "bk-3", holdId: null,
    draft: "Aldagi usually covers about 60% of Holter monitoring, so you would pay roughly \u20BE60. I\u2019ll confirm the exact figure with our desk and come straight back to you today.",
    handoff: {
      reason: "Rule: stop on anything about money or insurance",
      request: "How much will Aldagi cover for a 24h Holter?",
      summary: "Beka asked the price of 24h Holter monitoring (\u20BE150), then whether Aldagi insurance will cover part of it.",
      intent: "Price / insurance cover for Holter monitoring",
      urgency: "High — replied within 6 minutes expected",
      captured: [
        { key: "Service", value: "Holter monitoring, 24h", source: "customer" },
        { key: "Insurance", value: "Aldagi", source: "customer" },
      ],
      missing: ["Policy number", "Exact cover percentage"],
      history: "1 previous visit, no no-shows. Asked about Holter last month too.",
    },
    captured: [
      { key: "Service", value: "Holter monitoring, 24h", source: "customer" },
      { key: "Insurance", value: "Aldagi", source: "customer" },
    ],
    messages: [
      { id: uid("m"), from: "customer", channel: "WhatsApp", text: "how much is the holter for 24 hours?", ts: at(0, -0.4), status: "read" },
      { id: uid("m"), from: "ilia", channel: "WhatsApp", text: "\u20BE150 for 24 hours.", ts: at(0, -0.38), status: "read" },
      { id: uid("m"), from: "customer", channel: "WhatsApp", text: "my insurance is Aldagi, how much will they cover?", ts: at(0, -0.25), status: "read" },
      { id: uid("m"), from: "system", text: "Ilia stopped and handed to you \u2014 your rule is to stop on anything about money or insurance.", ts: at(0, -0.24), status: "read" },
    ],
  },

  // ---- JOURNEY 3: reschedule / cancellation ----
  {
    id: "conv-mari", customerId: "cust-mari", serviceId: "s-derma", channel: "Instagram", channels: ["Instagram"],
    lifecycle: "human", owner: "Giorgi Beridze", unread: true, urgency: "normal", waitingOn: "team",
    snoozedUntil: null, lastActivity: at(0, -1.5), journey: "reschedule", scriptStep: 0,
    bookingId: "bk-2", holdId: null, draft: null, handoff: null,
    captured: [{ key: "Booking", value: "Dermatology, Wed 11:00", source: "observed" }],
    messages: [
      { id: uid("m"), from: "customer", channel: "Instagram", text: "hi, something came up \u2014 can I move my dermatology appointment from Wednesday?", ts: at(0, -1.5), status: "read" },
      { id: uid("m"), from: "ilia", channel: "Instagram", text: "Of course, Mari. Let me pass you to the desk to find a new time.", ts: at(0, -1.48), status: "read" },
    ],
  },

  // ---- Supporting conversations for a lived-in inbox ----
  {
    id: "conv-giga", customerId: "cust-giga", channel: "WhatsApp", channels: ["WhatsApp"],
    lifecycle: "waiting_human", owner: "Giorgi Beridze", unread: false, urgency: "normal", waitingOn: "team",
    snoozedUntil: null, lastActivity: at(0, -0.4), journey: null, scriptStep: 0,
    bookingId: null, holdId: null,
    draft: "The full cardiac package is \u20BE850. We can split it into two payments \u2014 \u20BE450 at the first visit and \u20BE400 within 30 days. Shall I set that up?",
    handoff: {
      reason: "Rule: stop on anything about money or instalments",
      request: "Can I pay the \u20BE850 package in instalments?",
      summary: "Giga wants the full cardiac package but asked to split the \u20BE850 fee into two payments.",
      intent: "Instalment request",
      urgency: "Normal",
      captured: [{ key: "Interest", value: "Full cardiac package", source: "customer" }],
      missing: ["Preferred first date"],
      history: "2 previous visits, no no-shows.",
    },
    captured: [{ key: "Interest", value: "Full cardiac package", source: "customer" }],
    messages: [
      { id: uid("m"), from: "customer", channel: "WhatsApp", text: "can I pay the 850 package in two parts?", ts: at(0, -0.4), status: "read" },
      { id: uid("m"), from: "system", text: "Ilia stopped and handed to you \u2014 your rule is to stop on anything about money.", ts: at(0, -0.39), status: "read" },
    ],
  },
  {
    id: "conv-lasha", customerId: "cust-lasha", serviceId: "s-thyroid", channel: "Instagram", channels: ["Instagram", "WhatsApp"],
    lifecycle: "ilia", owner: "Ilia", unread: false, urgency: "low", waitingOn: "customer",
    snoozedUntil: null, lastActivity: at(0, -3), journey: null, scriptStep: 0,
    bookingId: null, holdId: null, draft: null, handoff: null,
    mergePending: { channel: "WhatsApp", reason: "same phone number" },
    captured: [{ key: "Interest", value: "Thyroid ultrasound", source: "customer" }],
    messages: [
      { id: uid("m"), from: "customer", channel: "Instagram", text: "thyroid ultrasound price?", ts: at(0, -3.2), status: "read" },
      { id: uid("m"), from: "ilia", channel: "Instagram", text: "A thyroid ultrasound is \u20BE75 and takes about 25 minutes. Would you like me to find a time?", ts: at(0, -3.18), status: "read" },
    ],
  },
  {
    id: "conv-salome", customerId: "cust-salome", serviceId: "s-cardio", channel: "WhatsApp", channels: ["WhatsApp"],
    lifecycle: "resolved", owner: "Ilia", unread: false, urgency: "low", waitingOn: null,
    snoozedUntil: null, lastActivity: at(-1, 16), journey: null, scriptStep: 0,
    bookingId: "bk-1", holdId: null, draft: null, handoff: null,
    captured: [{ key: "Service", value: "Cardiology consultation", source: "customer" }],
    messages: [
      { id: uid("m"), from: "customer", channel: "WhatsApp", text: "can I see Dr. Nino this week?", ts: at(-1, 15.9), status: "read" },
      { id: uid("m"), from: "ilia", channel: "WhatsApp", text: "I can offer today at 10:00, tomorrow at 11:30, or Saturday at 12:00. Which works best?", ts: at(-1, 15.94), status: "read" },
      { id: uid("m"), from: "customer", channel: "WhatsApp", text: "Today at 10:00 works for me.", ts: at(-1, 15.97), status: "read" },
      { id: uid("m"), from: "ilia", channel: "WhatsApp", text: "Confirmed for today at 10:00 with Dr. Nino. The booked value is \u20BE120. I will send a reminder before your visit.", ts: at(-1, 16), status: "read" },
    ],
  },
  demoConversation({
    id: "conv-ana-beridze", customerId: "cust-ana-beridze", serviceId: "s-derma", channel: "Instagram",
    day: -6, hour: 18, lifecycle: "resolved", owner: "Ilia", bookingId: "bk-30",
    customerText: "Could I arrange my annual skin check for next week?",
    responseText: "Yes — Tuesday at 14:00 is available with Dr. Lika. Your consultation is ₾90 and is now confirmed.",
    captured: [{ key: "Pattern", value: "Annual preventive skin check", source: "customer" }],
  }),
  demoConversation({
    id: "conv-luka-maisuradze", customerId: "cust-luka-maisuradze", serviceId: "s-cardio", channel: "WhatsApp",
    day: -5, hour: 10, lifecycle: "resolved", owner: "Ilia", bookingId: "bk-31",
    customerText: "Can I book my regular cardiology follow-up this week?",
    responseText: "Wednesday at 16:00 is available with Dr. Nino. I have confirmed it and will remind you the day before.",
  }),
  demoConversation({
    id: "conv-eka-khutsishvili", customerId: "cust-eka-khutsishvili", serviceId: "s-thyroid", channel: "Messenger",
    day: -8, hour: 13, lifecycle: "waiting_customer", owner: "Ilia", waitingOn: "customer",
    customerText: "I need another thyroid ultrasound. Do you have anything after work?",
    responseText: "I can offer Tuesday at 17:30, Thursday at 18:00, or Saturday at 16:30. Which time suits you?",
    captured: [{ key: "Preferred time", value: "After work", source: "customer" }],
  }),
  demoConversation({
    id: "conv-davit-abashidze", customerId: "cust-davit-abashidze", serviceId: "s-ecg", channel: "WhatsApp",
    day: -12, hour: 15, lifecycle: "resolved", owner: "Ilia", bookingId: "bk-13",
    customerText: "Thank you, can you send me the result from my ECG?",
    responseText: "The clinic desk has sent the report to your email. If you would like, I can also find a cardiology follow-up time.",
  }),
  demoConversation({
    id: "conv-tea-kvirikashvili", customerId: "cust-tea-kvirikashvili", serviceId: "s-derma", channel: "Instagram",
    day: -3, hour: 17, lifecycle: "resolved", owner: "Ilia",
    customerText: "The new time worked perfectly, thank you.",
    responseText: "You’re very welcome, Tea. Your visit is complete and the clinic is here whenever you need a follow-up.",
  }),
  demoConversation({
    id: "conv-giorgi-mchedlishvili", customerId: "cust-giorgi-mchedlishvili", serviceId: "s-holter", channel: "WhatsApp",
    day: -7, hour: 12, lifecycle: "waiting_human", owner: "Giorgi Beridze", waitingOn: "team", unread: true, urgency: "normal",
    customerText: "Does Ardi cover another Holter test if the symptoms came back?",
    draft: "I’ll check the exact repeat-test coverage with Ardi and confirm the amount you would pay before we schedule anything.",
    handoff: {
      reason: "Rule: stop on insurance coverage",
      request: "Does Ardi cover a repeat Holter test?",
      summary: "Giorgi previously completed a Holter test and says the symptoms have returned. He wants to understand Ardi coverage before booking another test.",
      intent: "Insurance coverage for repeat Holter monitoring",
      urgency: "Normal — symptoms mentioned, no emergency language detected",
      captured: [
        { key: "Service", value: "Holter monitoring, 24h", source: "customer" },
        { key: "Insurance", value: "Ardi", source: "customer" },
      ],
      missing: ["Policy number", "Referral requirement"],
      history: "1 attended Holter visit, no recorded no-shows.",
    },
    captured: [{ key: "Insurance", value: "Ardi", source: "customer" }],
  }),
  demoConversation({
    id: "conv-ketevan-nadiradze", customerId: "cust-ketevan-nadiradze", serviceId: "s-blood", channel: "Messenger",
    day: -1, hour: 11, lifecycle: "resolved", owner: "Ilia", bookingId: "bk-32",
    customerText: "Monday at 10 is good for the blood test.",
    responseText: "Confirmed for Monday at 10:00. The panel is ₾60 and I’ll send the preparation instructions here.",
  }),
  demoConversation({
    id: "conv-natia-shengelia", customerId: "cust-natia-shengelia", serviceId: "s-thyroid", channel: "Instagram",
    day: -2, hour: 19, lifecycle: "waiting_customer", owner: "Ilia", waitingOn: "customer",
    customerText: "Can I do the thyroid ultrasound in the afternoon?",
    responseText: "Wednesday at 14:00, Friday at 15:30, and Saturday at 13:00 are available. Which would you prefer?",
    bookingId: "bk-34", captured: [{ key: "Preferred time", value: "Afternoon", source: "customer" }],
  }),
  demoConversation({
    id: "conv-maia-gelashvili", customerId: "cust-maia-gelashvili", serviceId: "s-derma", channel: "Instagram",
    day: -4, hour: 18, lifecycle: "human", owner: "Tamuna Kvaratskhelia", waitingOn: "customer",
    customerText: "Can the doctor check both a mole and my scalp in one consultation?",
    responseText: "Yes, Dr. Lika can assess both during the same 30-minute consultation. Saturday at 10:00 is currently available.",
    responseFrom: "human", bookingId: "bk-36",
    captured: [{ key: "Consultation scope", value: "Mole and scalp", source: "customer" }],
  }),
  demoConversation({
    id: "conv-levan-chikovani", customerId: "cust-levan-chikovani", serviceId: "s-holter", channel: "WhatsApp",
    day: -3, hour: 14, lifecycle: "waiting_customer", owner: "Ilia", waitingOn: "customer",
    customerText: "Saturday afternoon is easiest. What times can you offer?",
    responseText: "I can offer Saturday at 14:00, 15:00, or 16:30. Choose one and I’ll ask you to confirm before booking.",
    bookingId: "bk-37", captured: [{ key: "Preferred time", value: "Saturday afternoon", source: "customer" }],
  }),
  demoConversation({
    id: "conv-elene-dolidze", customerId: "cust-elene-dolidze", serviceId: "s-blood", channel: "Instagram",
    day: -1, hour: 20, lifecycle: "ilia", owner: "Ilia", waitingOn: "customer", unread: true,
    customerText: "I missed the last appointment. Can you hold an evening slot for me?",
    responseText: "I can hold Monday at 16:00 for 15 minutes while you decide. Please confirm here and I’ll finalize it.",
    bookingId: "bk-39", captured: [{ key: "Recovery", value: "Requested another time after a no-show", source: "customer" }],
  }),
  demoConversation({
    id: "conv-salome-iashvili", customerId: "cust-salome-iashvili", serviceId: "s-cardio", channel: "Messenger",
    day: -18, hour: 10, lifecycle: "resolved", owner: "Ilia", bookingId: "bk-29",
    customerText: "I completed the consultation, but I’ll continue future care closer to my office.",
    responseText: "Understood, Salome. I’ve closed this enquiry. Your previous visit information remains available if you need it.",
    archivedAt: at(-17, 11), archivedBy: "Giorgi Beridze",
    captured: [{ key: "Outcome", value: "Future care closer to work", source: "customer" }],
  }),
];

// ---------------------------------------------------------------------------
// Automations (When -> If -> Then), Activation steps, Activity log
// ---------------------------------------------------------------------------
export const AUTOMATIONS = [
  { id: "a1", name: "Stop on money & insurance", when: "A customer mentions price, insurance or instalments", cond: "Any channel", then: "Hand off to a human with a suggested reply", active: true, tested: true },
  { id: "a2", name: "Low-confidence handoff", when: "Ilia is unsure how to answer", cond: "Confidence below threshold", then: "Ask a human before replying", active: true, tested: true },
  { id: "a3", name: "No-show recovery", when: "A booking is marked no-show", cond: "First no-show only", then: "Send a warm re-book message after 1 day", active: false, tested: false },
  { id: "a4", name: "Reminder the day before", when: "24h before a confirmed booking", cond: "Customer opted in to messaging", then: "Send a reminder on the original channel", active: true, tested: true },
];

export const ACTIVATION = [
  { id: "act-1", label: "Connect one channel", done: true, hint: "Instagram & WhatsApp connected" },
  { id: "act-2", label: "Import services", done: true, hint: "6 services imported from your ERP" },
  { id: "act-3", label: "Review exceptions", done: false, hint: "1 service needs a price check" },
  { id: "act-4", label: "Configure availability", done: true, hint: "Mon–Sat, 10:00–19:00" },
  { id: "act-5", label: "Define qualification & handoff rules", done: true, hint: "4 rules active" },
  { id: "act-6", label: "Invite team", done: false, hint: "Add your manager & agents" },
  { id: "act-7", label: "Run a test conversation", done: false, hint: "The real activation moment" },
  { id: "act-8", label: "Go live", done: false, hint: "Unlocks after a successful test" },
];

export const ACTIVITY = [
  { id: uid("act"), ts: at(0, -1.5), actor: "ilia", action: "Handed Mari Kapanadze to the desk", detail: "Reschedule request outside booking window", entity: "conv-mari", undoable: false },
  { id: uid("act"), ts: at(0, -0.24), actor: "automation", action: "Stopped on insurance question", detail: "Rule: money & insurance \u2192 Beka Lomidze", entity: "conv-beka", undoable: false },
  { id: uid("act"), ts: at(-1, 16), actor: "ilia", action: "Booked Salome Datunashvili", detail: "Cardiology consultation \u2014 today 10:00", entity: "bk-1", undoable: false },
  { id: uid("act"), ts: at(-2, 12.4), actor: "human", action: "Marked Lasha Gogichaishvili attended", detail: "Thyroid ultrasound \u2014 \u20BE75", entity: "bk-5", undoable: false },
];
